/**
 * CypAdhdDdBridge — presents a CYP ADHD patient's modular data as the legacy
 * PatientDataContext, so the ADULT Diagnostic Decision pages run UNCHANGED
 * for children (same pattern as CypAdhdPadBridge).
 *
 * The CYP DD pages are literal copies of the adult flat multi-step DD
 * (0_DDReview / 1_DD / 2_CSD / 3_Formulation — Eve/Sai, 28 Aug: match the
 * adult v2 workflow, never invent a new interface). Those pages read and
 * write the legacy monolithic dataModel through updatePatientFirestore.
 * CYP has no monolithic dataModel, so this provider:
 *
 *   READS — synthesises a patientDataModel from the modular stage docs:
 *     dataModel['2_clinicalInterview'].sections[12]  <- ADHD + the SSI's
 *       emerging differential labels (the condition list the DD decides on)
 *     dataModel['3_semiStructuredInterview']         <- the CYP SSI answers in
 *       the adult SECTIONS_SSI shape ("Symptoms" / "Domains of Functional
 *       Impairment" / internal case notes), with the CYP column semantics:
 *       Adulthood = the last six months, Childhood = before age 12 (implied
 *       by persistence for a child of 12 or under, matching the SSI's onset
 *       panel rule)
 *     dataModel['4_diagnosticDecision']              <- the stage doc's
 *       data.legacyModel (seeded on first open, hydrated from any canonical
 *       decision the earlier CYP DD wizard recorded)
 *     dataModel['6_manageReports']                   <- the SSI / DD
 *       reportContent docs' approval flags and outputs
 *
 *   WRITES — translates updatePatientFirestore(patientId, updates):
 *     '4_diagnosticDecision' -> deep-merged into the DD stage's
 *       data.legacyModel, AND mirrored into the canonical CYP fields the
 *       backend extractor reads (data.decision.*, data.dsmCriteria.*,
 *       data.otherConditions.*, data.finalFormulationNotes) so the report
 *       pipeline and every existing CYP consumer keep working
 *     '6_manageReports' dd* flags -> the DD reportContent doc, plus stage
 *       status 'completed' when ddReportGenerated lands
 *     '5_postAssessmentDiscussion' status writes -> dropped (CYP PAD
 *       unlocking is driven by the DD stage status, not a legacy PAD flag)
 */

import React, { useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { doc as firestoreDoc, updateDoc, setDoc } from 'firebase/firestore';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { db } from '../../../firebase';
import { getCypAdhdReportContentPathSegments } from '../../../constants/cypAdhdFirestorePaths';
import { deepMerge } from '../../../utils/deepMerge';
import { ADHD_CONDITION_LABEL } from './cypDdRouting';
import {
  CYP_SSI_SYMPTOMS,
  CYP_SSI_SETTINGS,
  CYP_SSI_IMPAIRMENT_DOMAINS,
  CYP_SSI_INCONCLUSIVE,
  differentialId,
} from '../22_cypAdhdSSI/config/cypAdhdSsiConfig';
import { CYP_NOTE_TAG_LABEL_BY_ID } from '../../../constants/cypAdhdNoteTagOptions';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';

/** Same computation as the SSI page's ageFromDateOfBirth (kept local so the
 * bridge does not import the whole SSI page module). */
function ageFromDateOfBirth(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
}

const DD_STAGE_KEY = 'diagnosticDecision';
const SSI_STAGE_KEY = 'semiStructuredInterview';

/** Adult 1_DD question key -> canonical CYP data.dsmCriteria key. */
const ADULT_TO_CYP_CRITERION = {
  // Criterion A on the page is age under 17 and the two six-symptom
  // thresholds. sixMonths and developmentallyInappropriate stay mapped so a
  // record that already has those answers still round-trips; the page no
  // longer asks them.
  dsmCriterionA_AgeUnder17: 'ageUnder17',
  dsmCriterionA_SymptomThreshold_A: 'inattentionThreshold',
  dsmCriterionA_SymptomThreshold_HI: 'hyperactivityThreshold',
  dsmCriterionA_SixMonths: 'sixMonths',
  dsmCriterionA_DevelopmentallyInappropriate: 'developmentallyInappropriate',
  dsmCriterionB_AgeOfOnset: 'onsetBeforeTwelve',
  dsmCriterionC_SymptomsAcrossSettings: 'acrossSettings',
  dsmCriterionD_FunctionalImpairment: 'functionalImpairment',
  dsmCriterionE_NotOtherExplanation: 'notBetterExplained',
};

/** Adult CSD decision label -> canonical CYP otherConditions outcome. */
const ADULT_TO_CYP_OUTCOME = {
  'Confirm diagnosis': 'Diagnosed',
  'Rule out': 'Not diagnosed',
  'Unable to confirm': 'Refer for further assessment',
};
const CYP_TO_ADULT_DECISION = Object.fromEntries(
  Object.entries(ADULT_TO_CYP_OUTCOME).map(([adult, cyp]) => [cyp, adult]),
);

/** CYP SSI setting key -> the adult presentInSettings label 1_DD counts. */
const SETTING_KEY_TO_ADULT_LABEL = {
  home: 'Familial',
  school: 'Educational',
  social: 'Social',
};

/** Flatten a structured-notes value (array or string) to display text. */
function flattenNotes(value) {
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return '';
  return value
    .map((note) => {
      const text = (note?.text || '').trim();
      if (!text) return null;
      const tags = (note?.criteriaTags || [])
        .map((id) => CYP_NOTE_TAG_LABEL_BY_ID[id])
        .filter(Boolean);
      return tags.length ? `[${tags.join(', ')}] ${text}` : text;
    })
    .filter(Boolean)
    .join('\n');
}

/**
 * Deep-clone a legacy tree, copying only plain objects/arrays. Firestore
 * Timestamps and FieldValue sentinels (serverTimestamp()) are kept by
 * reference — JSON round-tripping them corrupts both.
 */
function cloneTree(value) {
  if (Array.isArray(value)) return value.map(cloneTree);
  if (value && typeof value === 'object' && value.constructor === Object) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, cloneTree(v)]),
    );
  }
  return value;
}

/**
 * Adult-parity cleanup: keep only decisions for conditions still on the
 * current differential list (the adult page deletes stale
 * finalComorbidDiagnoses entries via a transaction; the bridge does it at
 * the synthesis/write boundary instead). Without this, a decision recorded
 * for a differential that was later removed on the SSI keeps flowing into
 * the report inputs labelled by its raw id.
 */
function filterFcdToCurrent(fcd, emergingDifferentials) {
  const currentLabels = new Set([
    ADHD_CONDITION_LABEL,
    ...(emergingDifferentials || []).map((d) => d.label),
  ]);
  return Object.fromEntries(
    Object.entries(fcd || {}).filter(([label]) => currentLabels.has(label)),
  );
}

/**
 * The CYP SSI outputs, re-keyed to the adult keys the copied 1_DD popup
 * reads (v2 layout: ssi_unified_3_1_{n} per symptom, ssi_unified_3_2_{i}
 * per impairment domain). Without this every symptom/domain detail popup
 * shows "Not available" over fully generated SSI paragraphs.
 */
function translateSsiOutputs(outputs) {
  const translated = { ...(outputs || {}) };
  CYP_SSI_SYMPTOMS.forEach((symptom) => {
    const sentence = (outputs || {})[`cyp_ssi_symptom_${symptom.number}_sentence`];
    if (sentence) translated[`ssi_unified_3_1_${symptom.number}`] = sentence;
  });
  CYP_SSI_IMPAIRMENT_DOMAINS.forEach((domain, index) => {
    const summary = (outputs || {})[domain.outputKey];
    if (summary) translated[`ssi_unified_3_2_${index + 1}`] = summary;
  });
  return translated;
}

/**
 * The CYP SSI stage rendered in the adult SECTIONS_SSI shape 1_DD and
 * 0_DDReview scan (sectionTitle lookups, DSM_code filters, per-question
 * symptomPresent_* flags).
 *
 * Column semantics carried across: symptomPresent_Adulthood is the CYP
 * "persisted six months" determination (the copied page labels the column
 * "Last 6 months"), symptomPresent_Childhood is "present before age 12" —
 * implied by persistence for a child of 12 or under, exactly as the SSI's
 * onset panel implies it (age >= 13 is asked, younger is inside the window).
 */
function buildSsiLegacyModel(ssiStage, asksOnsetQuestion) {
  const data = ssiStage?.data || {};
  const symptoms = data.symptoms || {};
  const impairment = data.functionalImpairment || {};

  // Undetermined answers pass through as null: the adult tables render null
  // as an em-dash but FALSE as a red "assessed and absent" cross, so mapping
  // an unanswered symptom to false would show the MDT a determination the
  // clinician never made.
  const asTristate = (stored) => (stored === true ? true : (stored === false ? false : null));
  // Symptom determinations also carry the SSI's third answer, Inconclusive
  // (7 Oct). It passes through as itself so the MDT sees it named rather than
  // as "Not recorded"; the DD counts only `=== true`, so it never counts.
  const asDetermination = (stored) => (
    stored === CYP_SSI_INCONCLUSIVE ? CYP_SSI_INCONCLUSIVE : asTristate(stored)
  );

  const symptomQuestions = {};
  CYP_SSI_SYMPTOMS.forEach((symptom) => {
    const stored = symptoms[String(symptom.number)] || {};
    const persisted = asDetermination(stored.persistedSixMonths);
    const settings = stored.settings || {};
    symptomQuestions[String(symptom.number)] = {
      DSM_code: symptom.dsmCode,
      DSM_symptom: symptom.name,
      symptomPresent_Adulthood: persisted,
      symptomPresent_Childhood: asksOnsetQuestion
        ? asDetermination(stored.presentBeforeTwelve)
        : persisted,
      presentInSettings_Adulthood: persisted === true
        ? CYP_SSI_SETTINGS
          .filter((s) => settings[s.key] === true)
          .map((s) => SETTING_KEY_TO_ADULT_LABEL[s.key])
          .filter(Boolean)
        : [],
    };
  });

  const domainQuestions = {};
  CYP_SSI_IMPAIRMENT_DOMAINS.forEach((domain, index) => {
    const stored = impairment[domain.key] || {};
    domainQuestions[String(index + 1)] = {
      DSM_symptom: domain.title,
      functioningReduced_Adulthood: asTristate(stored.isImpairedSelection),
      // The CYP SSI never assesses childhood impairment: null renders as an
      // em-dash, never as a red cross.
      functioningReduced_Childhood: null,
    };
  });

  return {
    status: ssiStage?.status || 'notStarted',
    firstCompletedAt: ssiStage?.firstCompletedAt || ssiStage?.completedAt || null,
    sections: {
      1: { sectionTitle: 'Symptoms', questions: symptomQuestions },
      2: { sectionTitle: 'Domains of Functional Impairment', questions: domainQuestions },
      3: {
        sectionTitle: 'Internal Case Notes',
        // useEmergingFormulations reads the SSI clinician's CONFIDENTIAL
        // notes from this field (the emerging formulation itself comes from
        // CI section 12 clinicianNotes — see the synthesis below).
        internalCaseNotes: flattenNotes(data.confidentialNotes),
      },
    },
  };
}

/**
 * Seed (or hydrate) the legacy 4_diagnosticDecision tree from the canonical
 * CYP DD fields. A patient whose decision was recorded through the earlier
 * CYP DD wizard has data.decision / data.dsmCriteria / data.otherConditions
 * but no legacyModel — hydrating from canonical means their recorded
 * decision appears in the copied adult pages instead of vanishing.
 */
function buildLegacySeed(ddData, emergingDifferentials) {
  const decision = ddData?.decision || {};
  const dsmCriteria = ddData?.dsmCriteria || {};
  const otherConditions = ddData?.otherConditions || {};

  const section1 = { sectionTitle: 'Diagnostic Decision' };
  Object.entries(ADULT_TO_CYP_CRITERION).forEach(([adultKey, cypKey]) => {
    const value = dsmCriteria[cypKey];
    if (value === 'met') section1[adultKey] = true;
    else if (value === 'notMet') section1[adultKey] = false;
  });
  [
    'diagnosisDecision', 'diagnosisPresentation', 'diagnosisSeverity',
    'diagnosisRationale', 'remissionSpecifier', 'supportingEvidence',
    'supportingEvidenceNotes',
    // Fields of the pre-October decision shape, still copied so a record
    // made under it keeps reading back.
    'partialDiagnosisClassification', 'otherSpecifiedReason',
    'otherSpecifiedReasonText', 'supportedByCollateral',
  ].forEach((key) => {
    if (decision[key] !== undefined && decision[key] !== null) {
      section1[key] = decision[key];
    }
  });
  if (decision.diagnosisDecision) {
    section1.status = 'completed';
    section1.progress = 100;
  }

  const finalComorbidDiagnoses = {};
  (emergingDifferentials || []).forEach((entry) => {
    const stored = otherConditions[entry.id];
    if (!stored?.outcome) return;
    finalComorbidDiagnoses[entry.label] = {
      decision: CYP_TO_ADULT_DECISION[stored.outcome] || stored.outcome,
      notes: typeof stored.notes === 'string' ? stored.notes : flattenNotes(stored.notes),
    };
  });

  return {
    stageTitle: 'Diagnostic Decision',
    mdtPlan: {},
    sections: {
      1: section1,
      2: { sectionTitle: 'Secondary Diagnoses', finalComorbidDiagnoses },
      3: {
        sectionTitle: 'Formulation',
        finalFormulation: flattenNotes(ddData?.finalFormulationNotes),
        // The raw structured-notes array as well as the flattened string: the
        // CYP Formulation page edits the notes themselves (the backend
        // extractor reads their source tags), while the flattened form stays
        // for anything that wants it as plain text.
        finalFormulationNotes: ddData?.finalFormulationNotes ?? null,
      },
    },
  };
}

/** Canonical mirror of a merged legacy tree — what the backend extractor reads. */
function buildCanonicalUpdates(mergedLegacy, emergingDifferentials) {
  const updates = {};
  const s1 = mergedLegacy?.sections?.['1'] || mergedLegacy?.sections?.[1] || {};

  [
    'diagnosisDecision', 'diagnosisPresentation', 'diagnosisSeverity',
    'diagnosisRationale', 'remissionSpecifier', 'supportingEvidence',
    'supportingEvidenceNotes',
    // Fields of the pre-October decision shape, still copied so a record
    // made under it keeps reading back.
    'partialDiagnosisClassification', 'otherSpecifiedReason',
    'otherSpecifiedReasonText', 'supportedByCollateral',
  ].forEach((key) => {
    if (s1[key] !== undefined) updates[`data.decision.${key}`] = s1[key];
  });

  Object.entries(ADULT_TO_CYP_CRITERION).forEach(([adultKey, cypKey]) => {
    if (s1[adultKey] === undefined) return;
    updates[`data.dsmCriteria.${cypKey}`] = s1[adultKey] === true
      ? 'met'
      : (s1[adultKey] === false ? 'notMet' : null);
  });

  const fcd = filterFcdToCurrent(
    (mergedLegacy?.sections?.['2'] || mergedLegacy?.sections?.[2] || {})
      .finalComorbidDiagnoses,
    emergingDifferentials,
  );
  // Rebuilt whole (not per-key dot-paths) so the canonical map can never
  // hold an outcome for a differential that has left the list.
  const otherConditions = {};
  Object.entries(fcd).forEach(([label, entry]) => {
    if (!entry?.decision) return;
    // Same id derivation the SSI used when the differential was recorded, so
    // the canonical outcome lands on the id the backend resolves to a label.
    const knownId = (emergingDifferentials || [])
      .find((d) => d.label === label)?.id || differentialId(label);
    otherConditions[knownId] = {
      outcome: ADULT_TO_CYP_OUTCOME[entry.decision] || entry.decision,
      notes: entry.notes || '',
    };
  });
  updates['data.otherConditions'] = otherConditions;

  const s3 = mergedLegacy?.sections?.['3'] || mergedLegacy?.sections?.[3] || {};
  if (s3.finalFormulation !== undefined) {
    // The adult page writes a plain string; the backend flatteners accept a
    // string alongside the structured-notes arrays (structured_notes_utils).
    updates['data.finalFormulationNotes'] = s3.finalFormulation;
  }

  return updates;
}

export default function CypAdhdDdBridge({ children }) {
  const {
    header,
    assessmentStages,
    reportContent,
    updateAssessmentStage,
    flushStageWrites,
    fetchPatientData,
    error,
  } = useContext(ModularPatientDataContext);

  const ddStage = assessmentStages?.[DD_STAGE_KEY];
  const ssiStage = assessmentStages?.[SSI_STAGE_KEY];
  const ciStage = assessmentStages?.clinicalInterview;
  const padStage = assessmentStages?.postAssessmentDiscussion;

  const emergingDifferentials = useMemo(
    () => (ssiStage?.data?.emergingDifferentials || []).filter((d) => d?.id && d?.label),
    [ssiStage?.data?.emergingDifferentials],
  );

  // Same rule as the SSI's onset panel: in the org's onset-pair mode (TOC,
  // TDI-205) every child is asked "present before 12"; otherwise 13 and over
  // are asked and a child of 12 or under is inside the window, so onset is
  // implied by persistence. Same UNKNOWN-AGE fallback as the SSI page too
  // (fail open — treat as asked): the SSI collects the onset answer when the
  // DOB is missing, and this bridge previously failed closed, so a recorded
  // "present before 12: No" would have been discarded and shown as a merged
  // green tick derived from persistence alone.
  // Claims are only the hook's legacy fallback for UNSTAMPED patients; CYP
  // patients are always stamped (ASMT-14), and the header carries the same
  // organisation, so the bridge stays free of any auth dependency.
  const wf = useWorkflowConfig(header, { organisation: header?.organisation });
  const age = ageFromDateOfBirth(header?.dateOfBirth);
  const asksOnsetQuestion = wf.cypUseOnsetPair
    || typeof age !== 'number'
    || age >= 13;

  // Seed the legacy tree once per stage doc that lacks it, hydrated from any
  // canonical decision already recorded (wizard-era patients).
  //
  // Only the clinician who can edit the DD stage writes the seed. Opening the
  // module is not the same as working on it: a previewer (?preview=true), a
  // non-assigned clinician, or a Support user merely looking at the MDT
  // Review must not mutate the stage doc. Support in particular has no write
  // permission on assessmentStages (firestore.rules), so an unguarded seed
  // surfaced as a permission-denied "changes may not have saved" toast.
  // Nothing is lost for read-only viewers: patientDataModel below already
  // falls back to the same in-memory seed when data.legacyModel is absent.
  const isPreviewMode = useIsPreviewMode();
  const canEdit = useCanEditStage(header, 'DD', { previewMode: isPreviewMode });
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!canEdit) return;
    if (!ddStage || ddStage?.data?.legacyModel) return;
    seededRef.current = true;
    updateAssessmentStage(DD_STAGE_KEY, {
      'data.legacyModel': buildLegacySeed(ddStage?.data, emergingDifferentials),
    });
  }, [canEdit, ddStage, emergingDifferentials, updateAssessmentStage]);

  const patientDataModel = useMemo(() => {
    if (!header) return null;
    const legacyModel = ddStage?.data?.legacyModel
      || buildLegacySeed(ddStage?.data, emergingDifferentials);
    const legacySections = legacyModel.sections || {};
    const legacySection2 = legacySections['2'] || legacySections[2] || {};
    const ssiReport = reportContent?.[SSI_STAGE_KEY] || {};
    const ddReport = reportContent?.[DD_STAGE_KEY] || {};
    return {
      ...header,
      // The CYP pipeline's outputs are v2-shaped (single unified column);
      // without this the copied popup falls back to the legacy two-column
      // layout and the v1 output keys.
      report_logic_version: 2,
      // True for a child the SSI never asked the onset question (under 13:
      // being inside the age-12 window IS onset). The DD criteria tables
      // read this to merge the duration and onset columns, so the summary
      // never shows a column for a question that was not asked. Absent on
      // adult patients.
      cypOnsetImplied: !asksOnsetQuestion,
      // Criterion A's "Age <17" pre-fill; null when the date of birth is
      // unknown so the clinician answers it rather than inheriting a guess.
      cypAgeUnder17: typeof age === 'number' ? age < 17 : null,
      dataModel: {
        '2_clinicalInterview': {
          status: ciStage?.status || 'notStarted',
          firstCompletedAt: ciStage?.firstCompletedAt || ciStage?.completedAt || null,
          sections: {
            12: {
              // The flat condition list the DD decides on: ADHD plus the
              // SSI's emerging differentials (adult reads these from the CI's
              // Differential Formulation section).
              differentialDiagnoses: [
                ADHD_CONDITION_LABEL,
                ...emergingDifferentials.map((d) => d.label),
              ],
              secondaryComorbidDiagnoses: [],
              provisionalPrimaryDiagnosis: ADHD_CONDITION_LABEL,
              // The SSI clinician's Emerging Formulation, where
              // useEmergingFormulations expects the single-clinician
              // formulation to live (CI section 12 'Additional Notes').
              clinicianNotes: {
                'Additional Notes': flattenNotes(ssiStage?.data?.emergingFormulation),
              },
            },
          },
        },
        '3_semiStructuredInterview': buildSsiLegacyModel(ssiStage, asksOnsetQuestion),
        '4_diagnosticDecision': {
          ...legacyModel,
          sections: {
            ...legacySections,
            // Adult-parity stale-entry cleanup at the read boundary.
            2: {
              ...legacySection2,
              finalComorbidDiagnoses: filterFcdToCurrent(
                legacySection2.finalComorbidDiagnoses,
                emergingDifferentials,
              ),
            },
          },
          status: ddStage?.status || 'notStarted',
          firstCompletedAt: ddStage?.firstCompletedAt || ddStage?.completedAt || null,
          lastCompletedAt: ddStage?.lastCompletedAt || null,
          lastCompletedBy: ddStage?.lastCompletedBy || null,
          lastCompletedByName: ddStage?.lastCompletedByName || null,
        },
        // Read-only view for resolvePadStatusOnDdComplete: a non-locked
        // status means DD completion never tries to rewrite PAD state (CYP
        // PAD unlocking is driven by the DD stage status).
        '5_postAssessmentDiscussion': {
          status: padStage?.status || 'notStarted',
          provisionalAdhdOutcome: ddStage?.data?.decision?.diagnosisDecision ?? null,
        },
        '6_manageReports': {
          ssiReportApprovedByName: ssiReport.reviewedByName || null,
          ssiReportApprovedAt: ssiReport.lastReviewedAt || null,
          ssiResetRequired: false,
          ddReportGenerated: ddReport.ddReportGenerated,
          ddReportGeneratedAt: ddReport.ddReportGeneratedAt,
          ddReportFirstGeneratedAt: ddReport.ddReportFirstGeneratedAt,
          ddReportApproved: ddReport.ddReportApproved,
          sections: {
            2: { outputs: translateSsiOutputs(ssiReport.outputs) },
            3: { stageSectionsGenerated: ddReport.stageSectionsGenerated || [] },
          },
        },
      },
    };
  }, [header, ddStage, ssiStage, ciStage, padStage?.status, reportContent,
    emergingDifferentials, asksOnsetQuestion]);

  // Base tree for write merges. Successive writes must chain on the LAST
  // MERGED tree, not on context state: the stage write is debounced and
  // last-write-wins per dot-path key, so two quick writes computed from the
  // same stale context base (e.g. two criterion clicks in one debounce
  // window) would silently drop the first.
  const legacyModelRef = useRef(null);

  // The legacy write API the copied pages call.
  const updatePatientFirestore = useCallback(async (pid, updates) => {
    const ddUpdate = updates?.['4_diagnosticDecision'];
    if (ddUpdate) {
      // Completion scalars belong on the STAGE DOC top level (the overview's
      // previously-completed bypass, appointment-date inference, and the
      // first-completion audit all read them there), never inside the legacy
      // tree.
      const {
        status, progress, docs_available: docsAvailable,
        lastCompletedAt, lastCompletedBy, lastCompletedByName,
        firstCompletedAt, firstCompletedBy, firstCompletedByName,
        ...treeUpdate
      } = ddUpdate;
      const current = legacyModelRef.current
        || ddStage?.data?.legacyModel
        || buildLegacySeed(ddStage?.data, emergingDifferentials);
      const merged = deepMerge(cloneTree(current), treeUpdate);
      if (merged.sections?.['2']?.finalComorbidDiagnoses) {
        merged.sections['2'].finalComorbidDiagnoses = filterFcdToCurrent(
          merged.sections['2'].finalComorbidDiagnoses, emergingDifferentials,
        );
      }
      legacyModelRef.current = merged;
      const stageUpdates = {
        'data.legacyModel': merged,
        ...buildCanonicalUpdates(merged, emergingDifferentials),
      };
      [
        ['lastCompletedAt', lastCompletedAt],
        ['lastCompletedBy', lastCompletedBy],
        ['lastCompletedByName', lastCompletedByName],
        ['firstCompletedAt', firstCompletedAt],
        ['firstCompletedBy', firstCompletedBy],
        ['firstCompletedByName', firstCompletedByName],
      ].forEach(([key, value]) => {
        if (value !== undefined) stageUpdates[key] = value;
      });
      if (status === 'completed') {
        stageUpdates.status = 'completed';
        stageUpdates.progress = 1;
      } else if (status === 'inProgress'
        && (ddStage?.status === 'notStarted' || !ddStage?.status)) {
        stageUpdates.status = 'inProgress';
      }
      updateAssessmentStage(DD_STAGE_KEY, stageUpdates);
    }

    const manageReports = updates?.['6_manageReports'];
    if (manageReports) {
      // dd* flags land on the DD reportContent doc — the same doc the CYP
      // review page and documents dashboard read.
      const ref = firestoreDoc(db, ...getCypAdhdReportContentPathSegments(pid, DD_STAGE_KEY));
      try {
        await updateDoc(ref, manageReports);
      } catch (e) {
        await setDoc(ref, manageReports, { merge: true });
      }
      if (manageReports.ddReportGenerated === true) {
        updateAssessmentStage(DD_STAGE_KEY, { status: 'completed', progress: 1 });
      }
    }
    // '5_postAssessmentDiscussion' status writes are intentionally dropped —
    // see the header comment.
  }, [ddStage, emergingDifferentials, updateAssessmentStage]);

  const bridgeValue = useMemo(() => ({
    patientDataModel,
    updatePatientFirestore,
    flushPendingUpdates: flushStageWrites,
    fetchPatientData,
    error,
  }), [patientDataModel, updatePatientFirestore, flushStageWrites, fetchPatientData, error]);

  return (
    <PatientDataContext.Provider value={bridgeValue}>
      {children}
    </PatientDataContext.Provider>
  );
}
